import { lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router';
import { Spinner, Toaster, TooltipProvider } from '@brimostech/ui';
import { AuthProvider } from './features/auth/auth-provider';
import { LoginPage } from './features/auth/login-page';
import {
  MFA_SETUP_PATH,
  RequireAuth,
  RequirePermission,
  RequireSuperadmin,
} from './features/auth/require-auth';
import { HomePage } from './features/home/home-page';
import { ForgotPasswordPage } from './features/identity/forgot-password-page';
import { InvitePage } from './features/identity/invite-page';
import { ResetPasswordPage } from './features/identity/reset-password-page';
import { AppShell } from './features/shell/app-shell';
import { NotFoundPage } from './features/shell/not-found-page';
import { queryClient } from './lib/query';
import { useTheme } from './lib/theme-context';
import { ThemeProvider } from './lib/theme-provider';

// Every page behind the shell loads on demand (the shell renders one fallback for all of them), so
// the first paint ships only the login, the shell and the home.
const MfaSetupPage = lazy(() =>
  import('./features/identity/mfa-setup-page').then((m) => ({ default: m.MfaSetupPage })),
);
const ProfilePage = lazy(() =>
  import('./features/identity/profile-page').then((m) => ({ default: m.ProfilePage })),
);
const UnitsPage = lazy(() =>
  import('./features/registry/units-page').then((m) => ({ default: m.UnitsPage })),
);
const UnitDetailPage = lazy(() =>
  import('./features/registry/unit-detail-page').then((m) => ({ default: m.UnitDetailPage })),
);
const BlocksPage = lazy(() =>
  import('./features/registry/blocks-page').then((m) => ({ default: m.BlocksPage })),
);
const PersonsPage = lazy(() =>
  import('./features/registry/persons-page').then((m) => ({ default: m.PersonsPage })),
);
const PersonDetailPage = lazy(() =>
  import('./features/registry/person-detail-page').then((m) => ({ default: m.PersonDetailPage })),
);
const VehiclesPage = lazy(() =>
  import('./features/registry/vehicles-page').then((m) => ({ default: m.VehiclesPage })),
);
const ParkingPage = lazy(() =>
  import('./features/registry/parking-page').then((m) => ({ default: m.ParkingPage })),
);
const InvitesPage = lazy(() =>
  import('./features/invites/invites-page').then((m) => ({ default: m.InvitesPage })),
);
const AccessPointsPage = lazy(() =>
  import('./features/registry/access-points-page').then((m) => ({ default: m.AccessPointsPage })),
);
const AccessRulesPage = lazy(() =>
  import('./features/access/access-rules-page').then((m) => ({ default: m.AccessRulesPage })),
);
const AlertsPage = lazy(() =>
  import('./features/alerts/alerts-page').then((m) => ({ default: m.AlertsPage })),
);
const CondominiumPage = lazy(() =>
  import('./features/condominium/condominium-page').then((m) => ({ default: m.CondominiumPage })),
);
const TotemPage = lazy(() =>
  import('./features/totem/totem-page').then((m) => ({ default: m.TotemPage })),
);
const FrontDeskPage = lazy(() =>
  import('./features/frontdesk/frontdesk-page').then((m) => ({ default: m.FrontDeskPage })),
);

// Rarely used or heavy pages (video, tables with many columns, operator portal) load on demand.
const EventsPage = lazy(() =>
  import('./features/access/events-page').then((m) => ({ default: m.EventsPage })),
);
const CamerasPage = lazy(() =>
  import('./features/registry/cameras-page').then((m) => ({ default: m.CamerasPage })),
);
const AnnouncementsPage = lazy(() =>
  import('./features/announcements/announcements-page').then((m) => ({
    default: m.AnnouncementsPage,
  })),
);
const PackagesPage = lazy(() =>
  import('./features/packages/packages-page').then((m) => ({ default: m.PackagesPage })),
);
const FinancePage = lazy(() =>
  import('./features/finance/finance-page').then((m) => ({ default: m.FinancePage })),
);
const VisitorsPage = lazy(() =>
  import('./features/visitors/visitors-page').then((m) => ({ default: m.VisitorsPage })),
);
const AuditPage = lazy(() =>
  import('./features/audit/audit-page').then((m) => ({ default: m.AuditPage })),
);
const IntercomLazyPage = lazy(() =>
  import('./features/intercom/intercom-page').then((m) => ({ default: m.IntercomPage })),
);
const OperatorPage = lazy(() =>
  import('./features/operator/operator-page').then((m) => ({ default: m.OperatorPage })),
);
const FleetPage = lazy(() =>
  import('./features/operator/fleet-page').then((m) => ({ default: m.FleetPage })),
);
const GatewayDetailPage = lazy(() =>
  import('./features/operator/gateway-detail-page').then((m) => ({
    default: m.GatewayDetailPage,
  })),
);
const OperatorAlertsPage = lazy(() =>
  import('./features/operator/operator-alerts-page').then((m) => ({
    default: m.OperatorAlertsPage,
  })),
);
const ReleasesPage = lazy(() =>
  import('./features/operator/releases-page').then((m) => ({ default: m.ReleasesPage })),
);

function Loading() {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <Spinner />
    </div>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/convite/:token?" element={<InvitePage />} />
      <Route
        path="/totem"
        element={
          <Suspense fallback={<Loading />}>
            <TotemPage />
          </Suspense>
        }
      />
      <Route element={<RequireAuth />}>
        <Route
          path={MFA_SETUP_PATH}
          element={
            <Suspense fallback={<Loading />}>
              <MfaSetupPage />
            </Suspense>
          }
        />
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="perfil" element={<ProfilePage />} />
          <Route path="unidades" element={<UnitsPage />} />
          <Route path="unidades/:id" element={<UnitDetailPage />} />
          <Route path="blocos" element={<BlocksPage />} />
          <Route path="pessoas" element={<PersonsPage />} />
          <Route path="pessoas/:id" element={<PersonDetailPage />} />
          <Route path="veiculos" element={<VehiclesPage />} />
          <Route path="vagas" element={<ParkingPage />} />
          <Route path="convites" element={<InvitesPage />} />
          <Route path="pontos-de-acesso" element={<AccessPointsPage />} />
          <Route
            path="cameras"
            element={
              <Suspense fallback={<Loading />}>
                <CamerasPage />
              </Suspense>
            }
          />
          <Route
            path="eventos"
            element={
              <RequirePermission permission="events:read">
                <Suspense fallback={<Loading />}>
                  <EventsPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route path="regras-de-acesso" element={<AccessRulesPage />} />
          <Route
            path="portaria"
            element={
              <RequirePermission permission="frontdesk:use">
                <Suspense fallback={<Loading />}>
                  <FrontDeskPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="interfone"
            element={
              <Suspense fallback={<Loading />}>
                <IntercomLazyPage />
              </Suspense>
            }
          />
          <Route
            path="alertas"
            element={
              <RequirePermission permission="alerts:read">
                <AlertsPage />
              </RequirePermission>
            }
          />
          <Route
            path="avisos"
            element={
              <RequirePermission permission="announcements:read">
                <Suspense fallback={<Loading />}>
                  <AnnouncementsPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="encomendas"
            element={
              <RequirePermission permission="packages:read">
                <Suspense fallback={<Loading />}>
                  <PackagesPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="financeiro"
            element={
              <RequirePermission permission="finance:read">
                <Suspense fallback={<Loading />}>
                  <FinancePage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="visitantes"
            element={
              <RequirePermission permission="visitors:read">
                <Suspense fallback={<Loading />}>
                  <VisitorsPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="auditoria"
            element={
              <RequirePermission permission="audit:read">
                <Suspense fallback={<Loading />}>
                  <AuditPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="condominio"
            element={
              <RequirePermission permission="condominium:settings">
                <CondominiumPage />
              </RequirePermission>
            }
          />
          <Route
            path="operador"
            element={
              <RequireSuperadmin>
                <Suspense fallback={<Loading />}>
                  <OperatorPage />
                </Suspense>
              </RequireSuperadmin>
            }
          />
          <Route
            path="operador/gateways"
            element={
              <RequireSuperadmin>
                <Suspense fallback={<Loading />}>
                  <FleetPage />
                </Suspense>
              </RequireSuperadmin>
            }
          />
          <Route
            path="operador/gateways/:id"
            element={
              <RequireSuperadmin>
                <Suspense fallback={<Loading />}>
                  <GatewayDetailPage />
                </Suspense>
              </RequireSuperadmin>
            }
          />
          <Route
            path="operador/alertas"
            element={
              <RequireSuperadmin>
                <Suspense fallback={<Loading />}>
                  <OperatorAlertsPage />
                </Suspense>
              </RequireSuperadmin>
            }
          />
          <Route
            path="operador/releases"
            element={
              <RequireSuperadmin>
                <Suspense fallback={<Loading />}>
                  <ReleasesPage />
                </Suspense>
              </RequireSuperadmin>
            }
          />
        </Route>
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
        <ThemedToaster />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
